// The Jev `state` and the three questions asked of it.
//
// Written against the live TypeSafe documentation (read 2026-09-21): State
// (https://docs.typesafe.ai/concepts/state), Primitives (https://docs.typesafe.ai/primitives), Choice,
// Noul, and Confidence. Four instructions from those pages shape everything below.
//
//  1. "Use an object for most requests so each part of the state has a descriptive name and its
//     relationships remain clear." So the state is a named JSON object, not a wall of prose, and every
//     field name says what the thing is rather than what the simulation calls it internally.
//
//  2. State is text only, and the model has no idea what a centimetre from the south-west corner of a
//     grid means. Every position is therefore translated into the two frames a staff officer actually
//     uses — which third of the corridor, and which side of which phase line — plus a distance in
//     kilometres where a distance is the thing being judged. Raw coordinates are omitted entirely:
//     they are noise that would cost tokens and buy a worse judgment.
//
//  3. "Question IDs are for code and are not sent to the model; include complete meaning in the
//     question." So each `instructions` string is self-contained, and `criteria` defines every option
//     in words that stand on their own.
//
//  4. "Ask independent questions over the same state together" — they run in parallel and cannot see
//     one another's answers. Main effort and reserve commitment are independent judgments over one
//     picture, so they go in one request; a contact's role is about one named contact, so it is asked
//     with that contact singled out in the question text.
//
// What is deliberately NOT in the state: anything the player cannot see. No enemy true position,
// strength, type or order. The input is `EstimatorContext`, which has no field that could carry one
// (see src/ai/copContext.ts). The instructor dials are in, because they are disclosed to the player on
// the scenario stage and in the OPORD — an S-2 in this game knows the enemy is expected to defend in
// depth; it does not know which platoon is the reserve.

import type { EstimatorContext, ThirdName } from './copContext';
import {
  contactWeightByThird, depthBandLabel, MANEUVER_TYPES, ownManeuverByThird, ownPastBandByThird,
  reserveTriggerBandId, thirdNameOf,
} from './copContext';
import { TICKS_PER_MIN } from '../sim/types';
import type { JevChoiceQuestion, JevNoulQuestion } from './jevProxyCore';

/** The four roles a contact can be judged into. `unknown` is the documented "include a no-match
 * outcome when nothing may fit" — a single stale 'unknown' blob 4 km out is not a screen, a main body
 * or a reserve, and forcing it into one of the three is how a classifier learns to lie. */
export type ContactRole = 'screen' | 'mainBody' | 'reserve' | 'unknown';

export const CONTACT_ROLES: readonly ContactRole[] = ['screen', 'mainBody', 'reserve', 'unknown'] as const;

/** Contacts and own platoons included in the state. Well under the 32K-token state budget, and past
 * this many the list is scrolling noise rather than evidence: the freshest are the ones that matter. */
const MAX_CONTACTS_IN_STATE = 24;
const MAX_OWN_UNITS_IN_STATE = 24;

const POSTURE_WORDS: Record<string, string> = {
  screen: 'expected to screen forward and delay, trading ground for time',
  depth: 'expected to defend in depth, holding a reserve back to counterattack',
  forward: 'expected to defend well forward, massed on the first phase line',
};

const EW_WORDS: Record<string, string> = {
  none: 'none',
  moderate: 'moderate jamming; reports and orders are slowed',
  heavy: 'heavy jamming; reports and orders are badly slowed',
};

function round(n: number, places = 0): number {
  const f = 10 ** places;
  return Math.round(n * f) / f;
}

function minutesSince(ctx: EstimatorContext, tick: number): number {
  return round(Math.max(0, (ctx.tick - tick) / TICKS_PER_MIN), 1);
}

/** The three third names actually in play for this corridor, in order across it. */
export function thirdOptions(ctx: EstimatorContext): ThirdName[] {
  return [...ctx.frame.names];
}

/** Distance in kilometres between two points held in centimetres. The grid's own `distCm` is not
 * available here (and would drag the simulation into the browser bundle for one card); a plane
 * distance is correct to well inside the error a contact already carries. */
function kmBetween(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = (a.x - b.x) / 100_000;
  const dy = (a.y - b.y) / 100_000;
  return Math.sqrt(dx * dx + dy * dy);
}

function objectiveCentre(ctx: EstimatorContext): { x: number; y: number } | null {
  const obj = ctx.graphics.find((g) => g.id === ctx.objectiveId) ?? ctx.graphics.find((g) => g.kind === 'obj');
  return obj && obj.pts.length > 0 ? obj.pts[0] : null;
}

/** One contact, as the state describes it. Exported so the test can read a field by name. */
export interface JevStateContact {
  contact: string;
  third: ThirdName;
  position: string;
  reported_type: string;
  position_error_m: number;
  km_to_objective: number | null;
  minutes_since_last_seen: number;
  minutes_since_first_reported: number;
}

export interface JevStateOwnUnit {
  callsign: string;
  type: string;
  third: ThirdName;
  position: string;
  strength_percent: number;
  in_contact: boolean;
}

export interface JevState {
  mission: string;
  area_of_operations?: string;
  clock: { elapsed_minutes: number; phase: string };
  corridor: { thirds: ThirdName[]; phase_lines: string[]; objective: string | null };
  conditions: {
    enemy_posture_briefed: string;
    electronic_warfare: string;
    time_of_day: string;
    command_and_control: string;
  };
  enemy_contacts: JevStateContact[];
  own_units: JevStateOwnUnit[];
  own_summary: {
    maneuver_platoons_by_third: Record<string, number>;
    platoons_past_reserve_trigger_line_by_third: Record<string, number>;
    reserve_trigger_line: string | null;
    contacts_held_by_third: Record<string, number>;
  };
}

const MISSION =
  'A blue (friendly) task force is attacking through a corridor to seize an objective. '
  + 'A red (enemy) force is defending it. The reader is the blue task force intelligence officer (S-2), '
  + 'working only from reported contacts: every enemy position below is a report with an error radius, '
  + 'and every enemy type below is an observer\'s guess that may be wrong or simply "unknown".';

/**
 * Builds the compact state one request is made against.
 *
 * Deterministic for a given context — the same picture always produces byte-identical state — which is
 * what makes the per-tick request cache in `src/ai/jevClient.ts` correct and what makes the calibration
 * harness reproducible.
 */
export function buildJevState(ctx: EstimatorContext): JevState {
  const thirds = thirdOptions(ctx);
  const objCentre = objectiveCentre(ctx);
  const objName = ctx.graphics.find((g) => g.id === ctx.objectiveId)?.name
    ?? ctx.graphics.find((g) => g.kind === 'obj')?.name
    ?? null;
  const triggerId = reserveTriggerBandId(ctx.frame);
  const triggerBand = ctx.frame.bands.find((b) => b.id === triggerId) ?? null;

  const contacts = [...ctx.contacts]
    .sort((a, b) => b.lastSeen - a.lastSeen || a.enemyId - b.enemyId)
    .slice(0, MAX_CONTACTS_IN_STATE)
    .map<JevStateContact>((c) => ({
      contact: contactLabel(c.enemyId),
      third: thirdNameOf(ctx.frame, c),
      position: depthBandLabel(ctx.frame, c),
      reported_type: describeTypeConf(c.typeConf),
      position_error_m: round(c.errorM),
      km_to_objective: objCentre ? round(kmBetween(c, objCentre), 1) : null,
      minutes_since_last_seen: minutesSince(ctx, c.lastSeen),
      minutes_since_first_reported: minutesSince(ctx, c.firstSeen),
    }));

  const own = ctx.own
    .filter((u) => u.alive && u.type !== 'uas')
    .sort((a, b) => a.id - b.id)
    .slice(0, MAX_OWN_UNITS_IN_STATE)
    .map<JevStateOwnUnit>((u) => ({
      callsign: u.callsign,
      type: u.type,
      third: thirdNameOf(ctx.frame, u),
      position: depthBandLabel(ctx.frame, u),
      strength_percent: round(u.strength),
      in_contact: u.inContact,
    }));

  const byThird = <T extends readonly number[]>(sums: T): Record<string, number> =>
    Object.fromEntries(thirds.map((name, i) => [name, round(sums[i] ?? 0, 1)]));

  return {
    mission: MISSION,
    area_of_operations: ctx.aoTitle,
    clock: { elapsed_minutes: round(ctx.elapsedMin), phase: ctx.phase },
    corridor: {
      thirds,
      phase_lines: ctx.frame.bands.filter((b) => b.kind === 'pl').map((b) => b.name),
      objective: objName,
    },
    conditions: {
      enemy_posture_briefed: POSTURE_WORDS[ctx.dials.red] ?? ctx.dials.red,
      electronic_warfare: EW_WORDS[ctx.dials.ew] ?? ctx.dials.ew,
      time_of_day: ctx.dials.tod,
      command_and_control: ctx.dials.c2,
    },
    enemy_contacts: contacts,
    own_units: own,
    own_summary: {
      maneuver_platoons_by_third: byThird(ownManeuverByThird(ctx)),
      platoons_past_reserve_trigger_line_by_third: triggerId
        ? byThird(ownPastBandByThird(ctx, triggerId))
        : {},
      reserve_trigger_line: triggerBand?.name ?? null,
      contacts_held_by_third: byThird(contactWeightByThird(ctx)),
    },
  };
}

/** A stable public label for a contact. Deliberately not the enemy unit id under another name: it is
 * only ever used to point a question at a row of the state the model can see. */
export function contactLabel(enemyId: number): string {
  return `CONTACT ${enemyId}`;
}

function describeTypeConf(t: string): string {
  if (t === 'unknown') return 'unknown - too far to classify';
  if (t === 'vehicle') return 'vehicle - seen, but the type could not be made out';
  return t;
}

// ---- the three questions ------------------------------------------------------------------------------

export const MAIN_EFFORT_QUESTION_ID = 'main_effort_third';
export const RESERVE_QUESTION_ID = 'reserve_commits_within_ten_minutes';
export const CONTACT_ROLE_QUESTION_ID = 'contact_role';

/** "Where is the enemy main effort?", as a Choice over the three thirds this corridor actually has. */
export function mainEffortQuestion(ctx: EstimatorContext): JevChoiceQuestion {
  const thirds = thirdOptions(ctx);
  const criteria: Record<string, string> = {};
  for (const name of thirds) {
    criteria[name] = `The enemy's main defensive effort is weighted in the ${name} third of the corridor: `
      + `that is where the bulk of its combat power is committed and where it intends to stop the attack. `
      + `Evidence would be contacts held there, contacts that are getting fresher rather than staler there, `
      + `and the friendly platoons in that third meeting resistance.`;
  }
  return {
    type: 'choice',
    instructions:
      'Reading only the reported enemy contacts and friendly positions in `enemy_contacts` and `own_units`, '
      + 'which third of the corridor holds the enemy main effort - the third where the enemy has weighted '
      + 'the bulk of its combat power to stop this attack? Weigh fresh contacts more heavily than stale '
      + 'ones, and remember that a contact position carries the error in `position_error_m` and a reported '
      + 'type may be "unknown".',
    criteria,
  };
}

/** "Will the enemy commit its reserve in the next ten minutes?", as a Noul. */
export function reserveQuestion(ctx: EstimatorContext): JevNoulQuestion {
  const trigger = ctx.frame.bands.find((b) => b.id === reserveTriggerBandId(ctx.frame));
  const line = trigger ? trigger.name : 'the middle phase line';
  return {
    type: 'noul',
    instructions:
      `Will the enemy commit its reserve - a held-back force thrown in to counterattack - within the next `
      + `ten minutes? The enemy is ${ctx.dials.red === 'depth' ? 'briefed to hold a reserve back in depth' : `briefed as: ${POSTURE_WORDS[ctx.dials.red] ?? ctx.dials.red}`}. `
      + `A doctrinal enemy commits when the attack has clearly picked a third and pushed strength past ${line} there. `
      + `\`own_summary.platoons_past_reserve_trigger_line_by_third\` says how many friendly maneuver platoons `
      + `have crossed it in each third, and \`clock.elapsed_minutes\` says how long the attack has been running.`,
    criteria: {
      true: 'Within the next ten minutes the enemy reserve moves: a previously uncommitted enemy force '
        + 'attacks or reinforces in one of the thirds.',
      false: 'The enemy reserve stays where it is for at least the next ten minutes, or the enemy has no '
        + 'reserve left to commit.',
    },
  };
}

/** "What is this contact?", as a Choice over the four roles, with the contact named in the question. */
export function contactRoleQuestion(ctx: EstimatorContext, enemyId: number): JevChoiceQuestion {
  const label = contactLabel(enemyId);
  return {
    type: 'choice',
    instructions:
      `In \`enemy_contacts\`, the row whose \`contact\` field is "${label}" is one reported enemy contact. `
      + `What role does that contact most likely play in the enemy defence? Judge it from where it sits `
      + `relative to the phase lines, how it was reported, how long ago it was first seen, and what else is `
      + `held around it. Its \`reported_type\` may be "unknown" or "vehicle"; that is an observation limit, `
      + `not evidence of what it is.`,
    criteria: {
      screen: 'A forward screening or reconnaissance element: thin, well forward of the main positions, '
        + 'there to see and to delay rather than to hold. Usually seen early and usually alone.',
      mainBody: 'Part of the main defensive body: sitting on or just behind the defended line, in company '
        + 'with other contacts, and the thing the attack will actually have to break through.',
      reserve: 'An uncommitted reserve held in depth: well behind the defended line, often seen late or not '
        + 'at all until it moves, positioned to counterattack whichever third the attack commits to.',
      unknown: 'There is not enough in the report to say - too stale, too far out, too large a position '
        + 'error, or nothing around it to read it against.',
    },
  };
}

/** The two picture-level questions, asked together over one state, as the docs recommend. */
export function pictureQuestions(ctx: EstimatorContext): Record<string, JevChoiceQuestion | JevNoulQuestion> {
  return {
    [MAIN_EFFORT_QUESTION_ID]: mainEffortQuestion(ctx),
    [RESERVE_QUESTION_ID]: reserveQuestion(ctx),
  };
}

/** A fingerprint of the picture, so two estimates taken at the same moment share one request.
 *
 * Built from the state the model actually sees rather than from the tick: two ticks that produce the
 * same state deserve the same answer, and a tick that changed nothing should not cost a call. */
export function stateKey(ctx: EstimatorContext): string {
  const state = buildJevState(ctx);
  return JSON.stringify(state);
}

/** How many maneuver platoons blue has left, for the heuristic's confidence and for the debrief copy. */
export function ownManeuverCount(ctx: EstimatorContext): number {
  return ctx.own.filter((u) => u.alive && MANEUVER_TYPES.has(u.type)).length;
}
